import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

import Tile from "shared/react_components/Tile";

import Comment from './Comment';
import { commentSeparator, thread } from "./styles/Thread.module.scss";

const Thread = ({
  threadHashid,
  following,
  comments,
  onFollowingToggle,
  onDelete,
  onSave,
  highlightCommentHashid,
  userMentionsData,
  onAudioJump,
  newCommentReferences,
  onRemoveNewCommentReferences,
  focusNewComment,
  header
}) => (
  <Tile body className={thread}>
    {header}
    {comments.map((
      { createdAt, editable, hashid, lastEditedAt, richText, createdByName, createdByDeleted, callRecordingRef, transcriptRef },
      index,
      allComments
    ) => (
      <Fragment key={`${hashid}_${createdAt}`}>
        <Comment
          createdAt={createdAt}
          editable={editable}
          hashid={hashid}
          threadHashid={threadHashid}
          lastEditedAt={lastEditedAt}
          following={following}
          onFollowingToggle={onFollowingToggle}
          onDelete={onDelete}
          onSave={onSave}
          richText={richText}
          createdByName={createdByName}
          createdByDeleted={createdByDeleted}
          callRecordingRef={callRecordingRef}
          transcriptRef={transcriptRef}
          highlight={hashid === highlightCommentHashid}
          onAudioJump={onAudioJump}
        />
        {index + 1 < allComments.length &&
          <div className={commentSeparator} />
        }
      </Fragment>
    ))}
    <Comment
      onSave={onSave}
      userMentionsData={userMentionsData}
      onAudioJump={onAudioJump}
      {...newCommentReferences}
      onRemoveReferences={onRemoveNewCommentReferences}
      focus={focusNewComment}
    />
  </Tile>
);

Thread.defaultProps = {
  newCommentReferences: {}
};

Thread.propTypes = {
  /**
   * Hashid of the thread
   */
  threadHashid: PropTypes.string,
  /**
   * Informs whether the user is following this thread.
   */
  following: PropTypes.bool,
  /**
   * Comments to be displayed in the thread
   */
  comments: PropTypes.arrayOf(PropTypes.shape({
    createdAt: PropTypes.string,
    editable: PropTypes.bool,
    hashid: PropTypes.string,
    lastEditedAt: PropTypes.string,
    richText: PropTypes.string,
    createdByName: PropTypes.string
  })).isRequired,
  /**
   * Callback for when a thread is followed.
   * It expects a Promise to be returned.
   * Takes the thread id as an argument.
   */
  onFollowingToggle: PropTypes.func,
  /**
   * Callback for when a comment is deleted.
   * It expects a Promise to be returned.
   * Takes the comment id as an argument
   */
  onDelete: PropTypes.func,
  /**
   * Callback for when a new comment is created or an existing comment is edited.
   * It expects a Promise to be returned.
   * Takes in an object with 2 keys, 'hashid' and 'richText'
   */
  onSave: PropTypes.func.isRequired,
  /**
   * Hashid of the current comment that should be highlighted
   */
  highlightCommentHashid: PropTypes.string,
  /**
   * Function that returns a Promise that returns suggested users to mention. See Comment for specific details.
   */
  userMentionsData: PropTypes.func,
  /**
   * Function that accepts an integer argument that determines what point in the audio player to jump to.
   */
  onAudioJump: PropTypes.func,
  /**
   * Hash of comment references to add to the new comment form area
   */
  newCommentReferences: PropTypes.object,
  /**
   * Function that is called when references on new comment are to be removed
   */
  onRemoveNewCommentReferences: PropTypes.func,
  /**
   * Boolean to determine if the new comment textarea should be focused
   */
  focusNewComment: PropTypes.bool,
  /**
   * Component that is positioned at the header of the Thread
   */
  header: PropTypes.object
};

export default Thread;
